import { Fragment, type ReactNode } from 'react';

export type Column<R> = {
  header: ReactNode;
  cell: (row: R) => ReactNode;
  align?: 'left' | 'right';
};

export function DataTable<R>({
  columns,
  rows,
  rowKey,
  empty,
  onRowClick,
  expandedContent,
}: {
  columns: Column<R>[];
  rows: R[];
  rowKey: (row: R) => string | number;
  empty?: ReactNode;
  /** Optional click handler; rows get a pointer cursor + hover tint when set. */
  onRowClick?: (row: R) => void;
  /** Optional per-row detail; a non-null return renders as a secondary row
   *  spanning every column directly beneath the row. */
  expandedContent?: (row: R) => ReactNode;
}): JSX.Element {
  if (rows.length === 0) {
    return <div className="text-sm text-gray-500 italic">{empty ?? 'No rows.'}</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-gray-500">
          {columns.map((c, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: columns are a static, ordered list
            <th key={i} className={`py-1 ${c.align === 'right' ? 'text-right' : ''}`}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const detail = expandedContent ? expandedContent(r) : null;
          return (
            <Fragment key={rowKey(r)}>
              <tr
                className={`border-b ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(r);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((c, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns are a static, ordered list
                  <td key={i} className={`py-1 ${c.align === 'right' ? 'text-right' : ''}`}>
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
              {detail != null && detail !== false && (
                <tr className="border-b bg-gray-50">
                  <td colSpan={columns.length} className="px-2 py-2">
                    {detail}
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
